/**
 * P-04 «Профиль пользователя» с действиями SF-01…SF-06.
 *
 * Изменение v1.5: экран собран из общих блоков профиля (`entities/user/
 * ProfileView`) — свой профиль (U-01) и чужой выглядят одинаково, как в
 * Telegram. Действия живут в ряду плиток: «Написать», «Ссылка», «Ещё».
 *
 * ПС §14: block и report доступны из профиля и из чата.
 * Критические действия подтверждаются (ПС §04).
 * После жалобы интерфейс предлагает блокировку как безопасный следующий шаг.
 */
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { VibePanel } from '@entities/candidate/VibePanel'
import { RHYTHM_ICON, RHYTHM_LABEL } from '@entities/candidate/vibe'
import { useConversationByCounterpart } from '@entities/conversation/model'
import {
  ProfileAction,
  ProfileActions,
  ProfileChips,
  ProfileHero,
  ProfileInfo,
  ProfileInfoRow,
  ProfileSection,
} from '@entities/user/ProfileView'
import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import {
  ActionItem,
  ActionList,
  Button,
  Chip,
  Dialog,
  EmptyState,
  Sheet,
  Spinner,
  useToast,
} from '@shared/ui'

import { conversationsRepo, uiStateRepo, UiKey } from '@shared/db'
import { haptic } from '@shared/lib/motion'
import { profileLink, usernameRules } from '@shared/lib/username'

import { useContactActions, useIsContact } from '@features/contacts/use-contacts'
import { ReportDialog } from '@features/report/ReportDialog'

import { Screen } from '../../layouts/Screen'
import { formatPresence } from '../../lib/format'
import { useAppStore } from '../../model/app-store'

export function UserProfileScreen(): React.ReactElement {
  const { userId } = useParams({ strict: false }) as { userId: string }
  const search = useSearch({ strict: false }) as { action?: 'report' | 'block' }
  const navigate = useNavigate()
  const toast = useToast()

  const config = useAppStore((s) => s.config)
  const myInterests = useAppStore((s) => s.me?.profile.interests ?? [])
  const conversation = useConversationByCounterpart(userId)
  // Изменение v1.6: контакт — явный список, как в Telegram
  const isContact = useIsContact(userId)
  const { add: addContact, remove: removeContact, busy: contactBusy } = useContactActions()

  const menuRef = useRef<HTMLSpanElement>(null)
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null)
  const [reportOpen, setReportOpen] = useState(search.action === 'report')
  const [blockOpen, setBlockOpen] = useState(search.action === 'block')
  const [unmatchOpen, setUnmatchOpen] = useState(false)
  const [offerBlock, setOfferBlock] = useState(false)
  const [opening, setOpening] = useState(false)

  const {
    data: card,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => unwrap(await api.GET('/users/{userId}', { params: { path: { userId } } })),
    retry: false,
  })

  useEffect(() => {
    if (search.action === 'report') setReportOpen(true)
    if (search.action === 'block') setBlockOpen(true)
  }, [search.action])

  if (isLoading) {
    return (
      <Screen title="Профиль" back>
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}>
          <Spinner size={28} />
        </div>
      </Screen>
    )
  }

  // ПС §10: нейтральное состояние без раскрытия причины.
  // M-04 «Профиль недоступен» и P-10 «Кандидат недоступен»: нейтральное
  // состояние без раскрытия причины (ПС §10).
  if (error || !card || card.availability !== 'available') {
    return (
      <Screen title="Профиль" back>
        <EmptyState
          icon="user"
          title="Профиль недоступен"
          text="Анкета скрыта или аккаунт больше не активен."
        />
      </Screen>
    )
  }

  const activeConversation = conversation != null && conversation.state === 'active' ? conversation : null
  const hasMatch = activeConversation !== null && activeConversation.matches.length > 0
  // BS §11.3 и изменение v1.6: писать можно по совпадению, по никнейму и своему контакту.
  const canWrite = activeConversation !== null || Boolean(card.username) || isContact

  /** Ссылка вида /@никнейм — и плитка «Ссылка», и строка никнейма (BS §11.3). */
  const copyLink = (): void => {
    const link = profileLink(card.username ?? '', usernameRules(config))
    void navigator.clipboard
      .writeText(link)
      .then(() => {
        haptic('light')
        toast.show('Ссылка на профиль скопирована')
      })
      .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
  }

  const openChat = async (): Promise<void> => {
    if (activeConversation) {
      void navigate({ to: '/chats/$conversationId', params: { conversationId: activeConversation.id } })
      return
    }
    setOpening(true)
    try {
      const created = unwrap(await api.POST('/conversations', { body: { user_id: userId } as never }))
      // Реплика — единственный источник экрана диалога: ответ кладётся до перехода.
      await conversationsRepo.put(created)
      void navigate({ to: '/chats/$conversationId', params: { conversationId: created.id } })
    } catch (error) {
      if (error instanceof ApiError && error.code === 'rate_limited') {
        toast.show('Слишком много новых диалогов — попробуйте позже', { tone: 'danger' })
      } else if (error instanceof ApiError && error.code === 'user_unavailable') {
        toast.show('Пользователь недоступен', { tone: 'danger' })
      } else {
        toast.show('Не удалось открыть диалог', { tone: 'danger' })
      }
    } finally {
      setOpening(false)
    }
  }

  return (
    <>
      <Screen title={card.display_name ?? 'Профиль'} back narrow>
        <ProfileHero
          name={card.display_name}
          age={card.age}
          photos={card.photos}
          status={card.presence ? formatPresence(card.presence) : null}
        />

        {/* Ряд плиток — как «позвонить / видео / ещё» в профиле Telegram */}
        <ProfileActions>
          {canWrite ? (
            <ProfileAction icon="chats" label="Написать" disabled={opening} onClick={() => void openChat()} />
          ) : null}
          {card.username ? <ProfileAction icon="link" label="Ссылка" onClick={copyLink} /> : null}
          {!isContact ? (
            <ProfileAction
              icon="user-plus"
              label="Добавить"
              disabled={contactBusy}
              onClick={() => void addContact(userId)}
            />
          ) : null}
          <ProfileAction
            icon="more"
            label="Ещё"
            onClick={() => setMenuAnchor(menuRef.current?.getBoundingClientRect() ?? null)}
          />
        </ProfileActions>
        <span ref={menuRef} aria-hidden />

        {/*
          Сведения одной карточкой «подпись сверху, значение снизу» — как блок
          username/bio в профиле Telegram. Нажатие на никнейм копирует ссылку.
        */}
        {card.username || card.phone || card.bio || card.occupation || card.city ? (
          <ProfileInfo>
            {card.username ? (
              <ProfileInfoRow
                label="никнейм"
                value={`@${card.username}`}
                accent
                trailingIcon="copy"
                onClick={copyLink}
              />
            ) : null}
            {/* Изменение v1.6: номер показывается по настройке владельца (BS §11.5) */}
            {card.phone ? (
              <ProfileInfoRow
                label="телефон"
                value={card.phone}
                accent
                trailingIcon="copy"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(card.phone ?? '')
                    .then(() => {
                      haptic('light')
                      toast.show('Номер скопирован')
                    })
                    .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
                }}
              />
            ) : null}
            {card.bio ? <ProfileInfoRow label="о себе" value={card.bio} multiline /> : null}
            {card.city ? <ProfileInfoRow label="город" value={card.city.name} /> : null}
            {card.occupation ? <ProfileInfoRow label="род занятий" value={card.occupation} /> : null}
          </ProfileInfo>
        ) : null}

        {card.interests?.length ? (
          <ProfileSection title="Интересы">
            <ProfileChips>
              {card.interests.map((interest) => (
                <Chip key={interest.id} static>
                  {interest.label}
                </Chip>
              ))}
            </ProfileChips>
          </ProfileSection>
        ) : null}

        {card.friends?.formats.length ? (
          <ProfileSection title="Какой движ">
            <ProfileChips>
              {card.friends.formats.map((format) => (
                <Chip key={format} static>
                  {format}
                </Chip>
              ))}
            </ProfileChips>
          </ProfileSection>
        ) : null}

        {card.friends?.rhythm?.length ? (
          <ProfileSection title="Когда свободно">
            <ProfileChips>
              {card.friends.rhythm.map((slot) => (
                <Chip key={slot} static icon={RHYTHM_ICON[slot]}>
                  {RHYTHM_LABEL[slot]}
                </Chip>
              ))}
            </ProfileChips>
          </ProfileSection>
        ) : null}

        {/* P-12: объяснимый подбор — пока кандидат в подборке (BS §11.4) */}
        {card.vibe ? (
          <ProfileSection title="Почему вы сойдётесь">
            <VibePanel
              vibe={card.vibe}
              card={card}
              myInterests={myInterests}
              onCopyIcebreaker={(text) => {
                void navigator.clipboard
                  .writeText(text)
                  .then(() => {
                    haptic('light')
                    toast.show('Фраза скопирована')
                  })
                  .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
              }}
            />
          </ProfileSection>
        ) : null}
      </Screen>

      {/* --- P-05: меню действий --- */}
      <Sheet
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        anchorRect={menuAnchor}
        title={card.display_name ?? 'Профиль'}
        flush
      >
        <ActionList>
          <ActionItem
            icon="flag"
            label="Пожаловаться"
            onClick={() => {
              setMenuAnchor(null)
              setReportOpen(true)
            }}
          />
          {isContact ? (
            <ActionItem
              icon="trash"
              label="Удалить из контактов"
              onClick={() => {
                setMenuAnchor(null)
                void removeContact(userId)
              }}
            />
          ) : null}
          {hasMatch ? (
            <ActionItem
              icon="close"
              label="Завершить совпадение"
              onClick={() => {
                setMenuAnchor(null)
                setUnmatchOpen(true)
              }}
            />
          ) : null}
          <ActionItem
            icon="block"
            label="Заблокировать"
            danger
            onClick={() => {
              setMenuAnchor(null)
              setBlockOpen(true)
            }}
          />
        </ActionList>
      </Sheet>

      {/* --- SF-01 / SF-02: жалоба --- */}
      <ReportDialog
        target={
          reportOpen
            ? {
                type: 'user',
                id: userId,
                // Имя не склоняется — оно идёт отдельной строкой описания, а не в заголовке
                title: 'Пожаловаться на профиль',
                subject: card.display_name ?? null,
              }
            : null
        }
        categories={config?.report_categories ?? []}
        onClose={() => setReportOpen(false)}
        onSubmitted={(reportId) => {
          setReportOpen(false)
          // GAP-ALL-04: id последней жалобы нужен экрану статуса SF-04.
          if (reportId) void uiStateRepo.set(UiKey.lastReportId, reportId)
          // ПС §14: после жалобы предлагаем блокировку как следующий шаг.
          setOfferBlock(true)
        }}
      />

      {/* --- SF-03: предложение блокировки после жалобы --- */}
      <Dialog
        open={offerBlock}
        onClose={() => setOfferBlock(false)}
        title="Жалоба отправлена"
        description="Заблокировать этого человека, чтобы он больше не мог с вами связаться?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOfferBlock(false)}>
              Не нужно
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setOfferBlock(false)
                setBlockOpen(true)
              }}
            >
              Заблокировать
            </Button>
          </>
        }
      />

      {/* --- SF-05: подтверждение блокировки --- */}
      <Dialog
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        dismissOnScrim={false}
        title={card.display_name ? `Заблокировать: ${card.display_name}?` : 'Заблокировать пользователя?'}
        description="Вы перестанете видеть друг друга, совпадение закроется, диалог станет недоступен. Снятие блокировки совпадение не вернёт."
        footer={
          <>
            <Button variant="secondary" onClick={() => setBlockOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                void api
                  .POST('/users/{userId}/block', {
                    params: { path: { userId } },
                    ...idempotency(uuid()),
                  })
                  .then(() => {
                    toast.show('Пользователь заблокирован')
                    void navigate({ to: '/dvizh' })
                  })
                  .catch(() => toast.show('Не удалось заблокировать', { tone: 'danger' }))
                setBlockOpen(false)
              }}
            >
              Заблокировать
            </Button>
          </>
        }
      />

      {/* --- SF-06: подтверждение unmatch --- */}
      <Dialog
        open={unmatchOpen}
        onClose={() => setUnmatchOpen(false)}
        dismissOnScrim={false}
        title="Завершить совпадение?"
        description="Диалог закроется, и какое-то время вы не будете попадать друг другу в подборку."
        footer={
          <>
            <Button variant="secondary" onClick={() => setUnmatchOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const matchId = conversation?.matches[0]?.id
                setUnmatchOpen(false)
                if (!matchId) return

                void api
                  .DELETE('/matches/{matchId}', {
                    params: { path: { matchId } },
                    ...idempotency(uuid()),
                  })
                  .then(() => {
                    toast.show('Совпадение завершено')
                    void navigate({ to: '/contacts' })
                  })
                  .catch(() => toast.show('Не удалось завершить совпадение', { tone: 'danger' }))
              }}
            >
              Завершить
            </Button>
          </>
        }
      />
    </>
  )
}
